import { useEffect, useState } from "react";

const STORAGE_KEY = "table-skeleton-states";

type RouteState = {
  count: number | null;
  lastUpdated: number;
};

type GlobalTableState = {
  [routePath: string]: RouteState;
};

type TableStateOptions = {
  staleDuration?: number;
};

export function useTableSkeletonState(
  routePath: string,
  options: TableStateOptions = {}
) {
  const { staleDuration = 1000 * 60 * 5 } = options; // 5 minutes default
  const [isLoading, setIsLoading] = useState(true);
  const [rowCount, setRowCount] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const allStates = JSON.parse(stored) as GlobalTableState;
        const routeState = allStates[routePath];

        if (routeState) {
          const isStale = Date.now() - routeState.lastUpdated > staleDuration;
          if (!isStale) {
            setRowCount(routeState.count);
          }
        }
      }
    } catch (error) {
      console.error("Error reading table states:", error);
    }

    setIsLoading(false);
  }, [routePath, staleDuration]);

  const updateRowCount = (count: number) => {
    setRowCount(count);

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const allStates = stored ? (JSON.parse(stored) as GlobalTableState) : {};

      const updatedStates = {
        ...allStates,
        [routePath]: {
          count,
          lastUpdated: Date.now(),
        },
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedStates));
    } catch (error) {
      console.error("Error saving table states:", error);
    }
  };

  const clearState = () => {
    setRowCount(null);
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const allStates = JSON.parse(stored) as GlobalTableState;
        delete allStates[routePath];

        if (Object.keys(allStates).length === 0) {
          localStorage.removeItem(STORAGE_KEY);
        } else {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(allStates));
        }
      }
    } catch (error) {
      console.error("Error clearing table state:", error);
    }
  };

  const clearAllStates = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing all table states:", error);
    }
  };

  return {
    isLoading,
    rowCount,
    updateRowCount,
    clearState,
    clearAllStates,
  };
}
